import { computePosition, flip, offset, shift, size } from "@floating-ui/dom";
import { clamp, debounce } from "lodash-es";
import type { StateCreator, StoreApi } from "zustand";

import {
  CLICK_THRESHOLD,
  HORIZONTAL_SCROLLBAR_HEIGHT,
  LONG_PRESS_TIMEOUT,
  MAX_BASE_PER_PIXEL,
  MIN_BASE_PER_PIXEL,
  REGION_DRAG_INDICATOR_WIDTH,
  VERTICAL_SCROLLBAR_WIDTH,
} from "../../settings.ts";
import { getHoveredTrack } from "../../utils/graphics/get-hovered-track.ts";
import {
  getCanvasCoordinates,
  getHorizontalScrollbarWidth,
  getHorizontalScrollbarX,
  getVerticalScrollbarHeight,
  getVerticalScrollbarY,
  isHorizontalScrollbarClicked,
  isVerticalScrollbarClicked,
} from "../../utils/graphics/scrollbar-graphics.ts";
import { getTrackOffsetY } from "../../utils/graphics/track-offset.ts";
import type { GenomeViewerState, GenomeViewerStore } from "../store.ts";

// Pointer event state variables
let pointerStartPos: { x: number; y: number } | undefined = undefined;
let longPressTimeoutRef = 0;

function isChildOf(node: HTMLElement | null, el: HTMLElement) {
  if (node == null || node === document.body) {
    return false;
  }

  if (node === el) {
    return true;
  } else {
    return isChildOf(node.parentElement, el);
  }
}

export interface UserEventsState {
  /** Pointer event state */
  isPointerDown: boolean;
  /** Wheel event state */
  isScrolling: boolean;
  /** Pointer event state */
  isDragging: boolean;
  /** Drawing setting */
  isVerticalScrolling: boolean;
  /** Drawing setting */
  isHorizontalScrolling: boolean;
  /** Drawing setting */
  lastX: number;
  /** Drawing setting */
  lastY: number;
  /** Drawing setting */
  scrollbarClickOffset: number;
  hoveringHorizontalScrollbar: boolean;
  hoveringVerticalScrollbar: boolean;
  disableKeyboardShortcuts: boolean;
}

export interface UserEventsActions {
  handleScroll: (deltaX: number, deltaY: number, isWheel?: boolean) => void;
  handleClick: (event: PointerEvent, mutableStoreUpdate: Partial<GenomeViewerState>) => void;
  handleZoom: (zoomFactor: number, centerX: number, alignLeft?: boolean) => void;
  handleDoubleClick: (event: PointerEvent) => Promise<void>;
  handleWheel: (e: WheelEvent) => void;
  handlePointerDown: (e: PointerEvent) => void;
  handlePointerMove: (e: PointerEvent | WheelEvent) => void;
  handlePointerUp: (e: PointerEvent) => void;
  handlePointerCancel: (e: PointerEvent) => void;
  handleScrollbarMouseMove: (e: PointerEvent) => void;
  handleScrollbarMouseUp: () => void;
  updateHoveredTrack: (e: PointerEvent | WheelEvent) => void;
  setupUserEventListeners: () => void;
  disableUserEventListeners: () => void;
  handleKeyDown: (e: KeyboardEvent) => void;
  handleKeyUp: (e: KeyboardEvent) => void;
  handleFeaturePopover: (e: PointerEvent) => void;
}

export type UserEventsSlice = UserEventsActions & UserEventsState;

export const initialUserEventsState: UserEventsState = {
  isScrolling: false,
  isPointerDown: false,
  isDragging: false,
  isVerticalScrolling: false,
  isHorizontalScrolling: false,
  lastX: 0,
  lastY: 0,
  scrollbarClickOffset: 0,
  hoveringHorizontalScrollbar: false,
  hoveringVerticalScrollbar: false,
  disableKeyboardShortcuts: false,
};

export const createUserEventsSlice: StateCreator<GenomeViewerStore, [], [], UserEventsSlice> = (set, get) => ({
  ...initialUserEventsState,

  handleScroll(deltaX: number, deltaY: number, isWheel = false) {
    const {
      trackGroups,
      asynchronousScrolling,
      hoveredGroupId,
      basePerPixel,
      scrollX,
      scrollY,
      canvas,
      postTrackSelectionMessages,
      trackGroupsInSelection,
      selectedRange,
    } = get();
    const allTracksHeight = get().getAllTracksHeight();

    const scrollUpdate = { scrollX, scrollY };

    if (asynchronousScrolling) {
      if (hoveredGroupId) {
        const activeTrackGroup = trackGroups.get(hoveredGroupId)!;
        const deltaBase = deltaX * basePerPixel;
        const newBaseShift = activeTrackGroup.graphics.baseShift - deltaBase;

        activeTrackGroup.graphics.baseShift = newBaseShift;

        if (isWheel) {
          scrollUpdate.scrollY = Math.floor(Math.max(0, Math.min(scrollY + deltaY, allTracksHeight - canvas!.height)));
        }
      }
      postTrackSelectionMessages(trackGroupsInSelection, selectedRange);
      handleScrollingEnd(set);
      set({ isScrolling: true });
    } else {
      scrollUpdate.scrollX = Math.floor(scrollX + deltaX);
      scrollUpdate.scrollY = Math.floor(Math.max(0, Math.min(scrollY + deltaY, allTracksHeight - canvas!.height)));

      if (scrollUpdate.scrollX != get().scrollX) {
        handleScrollingEnd(set);
        set({ isScrolling: true });
      }
      // Don't force re-render of the overview in case of asynchronous scrolling (already handled by the TrackGroupItem)
      get().forceOverviewRender();
    }

    set(scrollUpdate);

    get().refreshTracks(); // new imageBitmap
    get().refreshMain(); // move the coordinates as soon as possible
  },

  handleClick(event: PointerEvent, mutableStoreUpdate: Partial<GenomeViewerState>) {
    const {
      contextualMenuData,
      hoverData,
      trackGroupsInSelection,
      featureSelection,
      postTrackSelectionMessages,
      refreshMain,
    } = get();
    mutableStoreUpdate.selectedRange = undefined;

    // Click for selection of a feature
    if ("button" in event && event.button === 0) {
      if (hoverData?.id) {
        const hoverDataId = hoverData.id;
        const trackId = hoverData.trackId;
        const selection = featureSelection.get(trackId);
        let newSelection: number[];
        let hasLatestFeatureSelected = false;

        // There is already a selection of features in the clicked track
        if (selection) {
          newSelection = selection;

          // If the feature is already selected
          if (selection.includes(hoverDataId)) {
            if (event.ctrlKey) {
              // With Ctrl we remove it
              newSelection = selection.filter((featureId) => featureId !== hoverDataId);
            } else {
              // With normal click we clear the selection
              featureSelection.clear();
              newSelection = [];
            }
          } else {
            // Else add it to the selection
            hasLatestFeatureSelected = true;
            if (event.ctrlKey) {
              // With Ctrl we add it to the selection
              newSelection.push(hoverDataId);
            } else {
              // With normal click we start a new selection
              featureSelection.clear();
              newSelection = [hoverDataId];
            }
          }
        } else {
          if (!event.ctrlKey) {
            featureSelection.clear();
          }
          newSelection = [hoverDataId];
          hasLatestFeatureSelected = true;
        }
        if (newSelection.length > 0) {
          featureSelection.set(trackId, newSelection);
        } else {
          featureSelection.delete(trackId);
        }

        // Handle latest selection for table scroll to index
        const track = get().tracks.get(trackId);
        if (track) {
          if (hasLatestFeatureSelected) {
            set({
              latestSelectedFeature: {
                featureId: hoverDataId,
                trackType: track.trackType,
                subTypeId: track.subType?.id,
              },
            });
          } else {
            set({ latestSelectedFeature: undefined });
          }
        }
        set({
          featureSelection: new Map(featureSelection), // Mandatory to mutate the object in order to sync selection with table (deep change needs to be)
        });

        postTrackSelectionMessages(trackGroupsInSelection, undefined);
      }
    }

    // Right click action to open the contextual menu
    if ("button" in event && event.button === 2 && contextualMenuData?.id != undefined) {
      const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;

      mutableStoreUpdate.showFeaturePopover = true;

      Object.assign(contextualMenu.style, {
        display: "block",
      });

      const virtualEl = {
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: event.clientX,
            y: event.clientY,
            left: event.clientX,
            right: event.clientX,
            top: event.clientY,
            bottom: event.clientY,
          };
        },
      };

      computePosition(virtualEl, contextualMenu, {
        placement: "right-start",
        middleware: [
          offset(5),
          flip(),
          shift(),
          size({
            boundary: document.body,
            apply({ availableHeight, availableWidth, elements, y }) {
              // Change styles, e.g.
              Object.assign(elements.floating.style, {
                maxWidth: `${Math.max(0, Math.floor(availableWidth - 10))}px`,
                maxHeight: `${Math.max(0, Math.floor(availableHeight - y - 10))}px`,
              });
            },
          }),
        ],
      }).then(({ x, y }) => {
        Object.assign(contextualMenu.style, {
          top: `${y}px`,
          left: `${x}px`,
        });
      });
    } else {
      refreshMain();
    }
  },

  handleZoom(zoomFactor: number, centerX: number, alignLeft = false) {
    const { basePerPixel, scrollX, zoomRange } = get();
    const updateStore: Partial<GenomeViewerState> = {};

    // Calculate new scale while enforcing limits
    const newBasePerPixel = clamp(basePerPixel * zoomFactor, MIN_BASE_PER_PIXEL, MAX_BASE_PER_PIXEL);

    // Adjust scrollX to keep the center point at the same position
    const centerCoordinateX = (scrollX + centerX) * basePerPixel;

    const newScrollX = centerCoordinateX / newBasePerPixel - (alignLeft ? 0 : centerX);

    if (basePerPixel !== newBasePerPixel) {
      updateStore.scrollX = newScrollX;
      updateStore.basePerPixel = newBasePerPixel;
      updateStore.hoverData = undefined; // just remove it while zooming, because it's outdated now. Has to move the mouse to trigger it again though.
    }

    if (newBasePerPixel <= MIN_BASE_PER_PIXEL && zoomRange) {
      updateStore.scrollX = newScrollX;
    }

    get().forceOverviewRender();
    set(updateStore);
    get().refreshTracks();
    get().refreshMain();
  },

  async handleDoubleClick(_event: PointerEvent) {
    const { hoverData, tracks, trackGroups, align } = get();
    if (hoverData && hoverData.id && hoverData.trackId && hoverData.orthologId && hoverData.start && hoverData.stop) {
      const trackGroupId = tracks.get(hoverData.trackId)?.groupId;
      if (trackGroupId) {
        const contigId = trackGroups.get(trackGroupId)?.contigId;
        if (contigId) {
          // If we have hover data, we can use it for the double click action
          align({
            featureId: hoverData.id,
            trackId: hoverData.trackId,
            familyId: hoverData.orthologId,
            trackGroupId,
            contigId,
            start: hoverData.start,
            stop: hoverData.stop,
          });
        }
      }
    }
  },

  updateHoveredTrack(e: PointerEvent | WheelEvent) {
    const { trackGroups, tracks, scrollY, hoveredGroupId, hoveredTackId, canvas, postTrackHoverMessage } = get();
    const { x, y } = getCanvasCoordinates(e, canvas!.getBoundingClientRect());
    const hoveredTrack = getHoveredTrack(trackGroups, tracks, y + scrollY);
    if (hoveredTrack) {
      const { groupId: newHoveredGroupId, trackId: newHoveredTackId } = hoveredTrack;

      // Handle hover state, e.g., change cursor or highlight track
      // You might want to store the hovered track in the state
      if (newHoveredGroupId !== hoveredGroupId) {
        set({ hoveredGroupId: newHoveredGroupId });
      }

      if (newHoveredTackId !== hoveredTackId) {
        set({ hoveredTackId: newHoveredTackId });
      }

      const localY = y - getTrackOffsetY(trackGroups, tracks, newHoveredTackId) + scrollY;

      postTrackHoverMessage(hoveredTrack, x, localY);
    } else if (hoveredGroupId !== undefined || hoveredTackId !== undefined) {
      set({ hoveredGroupId: undefined, hoveredTackId: undefined });
    }
  },
  handleWheel(e: WheelEvent) {
    e.preventDefault();
    if (get().zoomRange) {
      return;
    }

    // Closes the contextual menu when zooming/scrolling
    const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;
    Object.assign(contextualMenu.style, {
      display: "none",
    });
    set({ showFeaturePopover: false, contextualMenuData: undefined });
    if (e.ctrlKey) {
      const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
      get().handleZoom(zoomFactor, e.offsetX);

      handleScrollingEnd(set);
      set({ isScrolling: true });
    } else {
      get().handleScroll(e.deltaX, e.deltaY, true);
      get().updateHoveredTrack(e);
    }
  },

  handlePointerDown(e: PointerEvent) {
    if (!("button" in e)) return;

    const state = get();
    const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
    pointerStartPos = { x, y };

    const storeUpdate: Partial<GenomeViewerState> = {};

    // Right click
    if (e.button === 2) {
      const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;
      storeUpdate.showFeaturePopover = false;
      storeUpdate.zoomRange = undefined;
      storeUpdate.contextualMenuData = state.hoverData?.id ? { ...state.hoverData } : undefined;

      Object.assign(contextualMenu.style, {
        display: "none",
      });

      // Left click
    } else if (e.button === 0) {
      storeUpdate.isPointerDown = true;
      // Set selection start
      if (e.ctrlKey) {
        storeUpdate.selectedRange = undefined;
      } else if (e.shiftKey) {
        const startBase = Math.floor((x + state.scrollX) * state.basePerPixel);
        const startY = y + state.scrollY;

        const startTrackGroupIndex = state.getTrackGroupIndexByY(y);
        const trackGroupIds = [...state.trackGroups.keys()];

        const trackGroupsInSelection = startTrackGroupIndex
          ? trackGroupIds.slice(startTrackGroupIndex, startTrackGroupIndex + 1)
          : trackGroupIds;

        const newTrackGroupsInSelection = state.fullRangeSelection ? trackGroupIds : trackGroupsInSelection;
        storeUpdate.trackGroupsInSelection = newTrackGroupsInSelection;

        storeUpdate.selectedRange = { start: startBase, stop: startBase, startY: startY, stopY: startY };
        state.postTrackSelectionMessages(newTrackGroupsInSelection, {
          start: startBase,
          stop: startBase,
        });
      } else {
        longPressTimeoutRef = window.setTimeout(() => {
          set({ asynchronousScrolling: true });
          state.refreshMain();
        }, LONG_PRESS_TIMEOUT);
      }

      storeUpdate.leftSelectionDragging = false;
      storeUpdate.rightSelectionDragging = false;

      if (state.selectedRange) {
        if (state.leftSelectionHovering) {
          storeUpdate.leftSelectionDragging = true;
        }

        if (state.rightSelectionHovering) {
          storeUpdate.rightSelectionDragging = true;
        }
      }

      if (isVerticalScrollbarClicked(x, state.canvas!.width)) {
        storeUpdate.isVerticalScrolling = true;
        storeUpdate.scrollbarClickOffset =
          y - getVerticalScrollbarY(state.scrollY, state.getAllTracksHeight(), state.canvas!.height);
      } else if (isHorizontalScrollbarClicked(y, state.canvas!.height)) {
        const maxWidth = state.getMaxWidth()[1];
        storeUpdate.isHorizontalScrolling = true;
        storeUpdate.scrollbarClickOffset = x - getHorizontalScrollbarX(state.scrollX, maxWidth, state.canvas!.width);
      }

      if (!(state.isVerticalScrolling || state.isHorizontalScrolling)) {
        storeUpdate.lastX = e.clientX;
        storeUpdate.lastY = e.clientY;
        state.canvas!.setPointerCapture(e.pointerId);
      }
    }

    set(storeUpdate);
  },

  handlePointerMove(e: PointerEvent | WheelEvent) {
    const state = get();

    window.clearTimeout(longPressTimeoutRef);

    if (!state.canvas) {
      return;
    }

    const storeUpdate: Partial<GenomeViewerState> = {};

    const boundingRect = state.canvas!.getBoundingClientRect();
    const { x, y } = getCanvasCoordinates(e, boundingRect);

    if (pointerStartPos) {
      const dx = Math.abs(x - pointerStartPos.x);
      const dy = Math.abs(y - pointerStartPos.y);
      // Consider it as a "move" only if threshold is reached. If not, we don't execute the code below
      if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD) return;
    }

    storeUpdate.leftSelectionHovering = false;
    storeUpdate.rightSelectionHovering = false;

    // Drag should only start when the CLICK_THRESHOLD is reached, to prevent setting isDragging to true with a simple click on the canvas
    if (state.isPointerDown && !state.isDragging) {
      storeUpdate.isDragging = true;
    }

    if (state.selectedRange) {
      const startRangeX = state.selectedRange.start / state.basePerPixel - state.scrollX;
      const stopRangeX = state.selectedRange.stop / state.basePerPixel - state.scrollX;

      if (startRangeX - REGION_DRAG_INDICATOR_WIDTH <= x && x <= startRangeX + REGION_DRAG_INDICATOR_WIDTH) {
        storeUpdate.leftSelectionHovering = true;
      }

      if (stopRangeX - REGION_DRAG_INDICATOR_WIDTH <= x && x <= stopRangeX + REGION_DRAG_INDICATOR_WIDTH) {
        storeUpdate.rightSelectionHovering = true;
      }
    }

    if (state.isDragging) {
      const deltaX = e.clientX - state.lastX;
      const deltaY = e.clientY - state.lastY;

      storeUpdate.lastX = e.clientX;
      storeUpdate.lastY = e.clientY;
      storeUpdate.hoverData = undefined; // just remove it while dragging, because it's outdated now. Has to move the mouse to trigger it again though.

      if (state.selectedRange) {
        if (state.leftSelectionDragging) {
          const start = Math.ceil(
            ((x < state.canvas.width ? (x > 0 ? x : 0) : state.canvas.width) + state.scrollX) * state.basePerPixel,
          );

          storeUpdate.selectedRange = { ...state.selectedRange, start };
          state.postTrackSelectionMessages(state.trackGroupsInSelection, {
            start,
            stop: state.selectedRange.stop,
          });

          set(storeUpdate);
          state.refreshMain();
          return;
        }

        if (state.rightSelectionDragging) {
          const stop = Math.ceil(
            ((x < state.canvas.width ? (x > 0 ? x : 0) : state.canvas.width) + state.scrollX) * state.basePerPixel,
          );

          storeUpdate.selectedRange = { ...state.selectedRange, stop };
          state.postTrackSelectionMessages(state.trackGroupsInSelection, {
            start: state.selectedRange.start,
            stop,
          });

          set(storeUpdate);
          state.refreshMain();
          return;
        }
      }

      // Zoom selection
      if (e.ctrlKey) {
        // Moving this here to ensure that the user don't simply "click", now it is trigger only if there is a slight move of 6px in the x-axis
        storeUpdate.zoomRange = { startX: pointerStartPos?.x ?? x, stopX: pointerStartPos?.x ?? x };
        if (e.ctrlKey && state.zoomRange?.startX) {
          storeUpdate.zoomRange = { ...state.zoomRange, stopX: clamp(x, 0, state.canvas.width) };
        }
        set(storeUpdate);
        state.refreshMain();
        return;
      }

      // Region selection
      if (e.shiftKey && state.selectedRange?.start !== undefined) {
        const stop = Math.ceil(
          ((x < state.canvas.width ? (x > 0 ? x : 0) : state.canvas.width) + state.scrollX) * state.basePerPixel,
        );

        const stopY = y + state.scrollY;
        const trackGroupIds = [...state.trackGroups.keys()];

        const startTrackGroupIndex =
          state.getTrackGroupIndexByY(state.selectedRange.startY) ?? trackGroupIds.length - 1;
        const stopTrackGroupIndex = state.getTrackGroupIndexByY(stopY) ?? (y > 0 ? trackGroupIds.length - 1 : 0);

        const startIndex = Math.min(startTrackGroupIndex, stopTrackGroupIndex);
        const endIndex = Math.max(startTrackGroupIndex, stopTrackGroupIndex) + 1;
        const trackGroupsInSelection = trackGroupIds.slice(startIndex, endIndex);

        const newTrackGroupsInSelection = state.fullRangeSelection ? trackGroupIds : trackGroupsInSelection;
        storeUpdate.trackGroupsInSelection = newTrackGroupsInSelection;

        storeUpdate.selectedRange = { ...state.selectedRange, stop };
        state.postTrackSelectionMessages(newTrackGroupsInSelection, {
          start: state.selectedRange.start,
          stop,
        });

        set(storeUpdate);
        state.refreshMain();
        return;
      }

      if (!(state.isVerticalScrolling || state.isHorizontalScrolling)) {
        set(storeUpdate);
        get().handleScroll(-deltaX, -deltaY);
      }
    } else {
      get().updateHoveredTrack(e);
      set(storeUpdate);
      state.refreshMain();
      // We need to call refreshMain() to update the hover effect of the bars for the selection of a region
    }
  },

  handlePointerUp(e: PointerEvent) {
    const state = get();
    const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
    const storeUpdate: Partial<GenomeViewerState> = {
      isPointerDown: false,
      isDragging: false,
      isDraggingRegionIndicator: false,
      leftSelectionDragging: false,
      rightSelectionDragging: false,
      asynchronousScrolling: false,
    };

    window.clearTimeout(longPressTimeoutRef);

    // Zoom into selection
    if (state.zoomRange) {
      state.zoomToRange();
    }

    // Reset zoom selection
    storeUpdate.zoomRange = undefined;

    if (state.selectedRange) {
      storeUpdate.selectedRange = {
        ...state.selectedRange,
        stopY: y + state.scrollY,
      };
    }

    // Align the bases on the grid with the base shift of the track
    for (const trackGroup of state.trackGroups.values()) {
      // Baseshift is stored as a float when async scrolling to prevent snapping on the grid when moving a track
      trackGroup.graphics.baseShift = Math.round(trackGroup.graphics.baseShift);
    }

    if (pointerStartPos) {
      const dx = Math.abs(x - pointerStartPos.x);
      const dy = Math.abs(y - pointerStartPos.y);

      // If the mouse pointer didn't move much, we consider it to be a click
      // TODO we should still verify if we didn't click on the scroll bar or any elements above the tracks shapes.
      if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD) {
        get().handleClick(e, storeUpdate);
      } else {
        storeUpdate.contextualMenuData = undefined;
      }
    }

    set(storeUpdate);

    pointerStartPos = undefined;

    state.canvas!.releasePointerCapture(e.pointerId);

    // refresh selection, ...
    get().refreshTracks();
    get().refreshMain();
  },

  handlePointerCancel(_e: PointerEvent) {
    set({ isPointerDown: false, isDragging: false, asynchronousScrolling: false });
  },

  handleScrollbarMouseMove(e: PointerEvent) {
    const state = get();
    const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
    const storeUpdate: Partial<GenomeViewerState> = {};
    const [maxTrackGroupWidth, maxWidth] = get().getMaxWidth();

    if (state.canvas && (x > state.canvas.width || x < 0 || y > state.canvas.height || y < 0)) {
      storeUpdate.hoveredGroupId = undefined;
    }

    let newHoveringHorizontalScrollBar: boolean;
    let newHoveringVerticalScrollBar: boolean;

    // If the horizontal scrollbar is hovered
    if (y > state.canvas!.height - HORIZONTAL_SCROLLBAR_HEIGHT - 2 && y < state.canvas!.height) {
      newHoveringHorizontalScrollBar = true;
    } else {
      newHoveringHorizontalScrollBar = false;
    }

    // If the vertical scrollbar is hovered
    if (x > state.canvas!.width - VERTICAL_SCROLLBAR_WIDTH - 2 && x < state.canvas!.width) {
      newHoveringVerticalScrollBar = true;
    } else {
      newHoveringVerticalScrollBar = false;
    }

    if (
      (newHoveringHorizontalScrollBar !== state.hoveringHorizontalScrollbar ||
        newHoveringVerticalScrollBar !== state.hoveringVerticalScrollbar) &&
      !state.isDragging
    ) {
      storeUpdate.hoveringHorizontalScrollbar = newHoveringHorizontalScrollBar;
      storeUpdate.hoveringVerticalScrollbar = newHoveringVerticalScrollBar;
      get().refreshMain();
    }

    if (state.isVerticalScrolling) {
      const allTracksHeight = get().getAllTracksHeight();
      const scrollableHeight = allTracksHeight - state.canvas!.height;
      const scrollbarY = y - state.scrollbarClickOffset;
      const scrollRatio =
        scrollbarY /
        (state.canvas!.height - getVerticalScrollbarHeight(get().getAllTracksHeight(), state.canvas!.height));

      storeUpdate.scrollY = Math.floor(Math.max(0, Math.min(scrollRatio * scrollableHeight, scrollableHeight)));
    } else if (state.isHorizontalScrolling && maxWidth > (state.canvas?.width ?? 0)) {
      const scrollableWidth = maxWidth - state.canvas!.width;
      const scrollbarX = x - state.scrollbarClickOffset;
      const scrollRatio =
        scrollbarX / (state.canvas!.width - getHorizontalScrollbarWidth(maxWidth, state.canvas!.width));
      storeUpdate.scrollX = clamp(
        Math.floor(scrollRatio * scrollableWidth),
        state.getOriginOffset(),
        Math.max(maxTrackGroupWidth - state.ctx!.canvas.width, state.scrollX),
      );
      handleScrollingEnd(set);
      set({ isScrolling: true });
    }

    set(storeUpdate);

    // No need to call refreshMain() when we change hoveredGroupId because we don't render hover effect on the canvas
    if (storeUpdate.scrollX != null || storeUpdate.scrollY != null) {
      get().refreshTracks();
    }
  },

  handleScrollbarMouseUp() {
    set({
      isVerticalScrolling: false,
      isHorizontalScrolling: false,
      isDraggingRegionIndicator: false,
    });
  },

  setupUserEventListeners() {
    const { canvas } = get();
    document.addEventListener("keydown", get().handleKeyDown);
    document.addEventListener("keyup", get().handleKeyUp);
    document.addEventListener("pointermove", get().handleScrollbarMouseMove);
    document.addEventListener("pointerup", get().handleScrollbarMouseUp);

    canvas!.addEventListener("wheel", get().handleWheel);
    canvas!.addEventListener("pointerdown", get().handlePointerDown);
    canvas!.addEventListener("pointermove", get().handlePointerMove);
    canvas!.addEventListener("pointerup", get().handlePointerUp);
    canvas!.addEventListener("pointercancel", get().handlePointerCancel);
    canvas!.addEventListener("contextmenu", (e) => {
      e.preventDefault();
    });
  },

  disableUserEventListeners() {
    const { canvas } = get();
    document.removeEventListener("keydown", get().handleKeyDown);
    document.removeEventListener("keyup", get().handleKeyUp);
    document.removeEventListener("pointermove", get().handleScrollbarMouseMove);
    document.removeEventListener("pointerup", get().handleScrollbarMouseUp);

    canvas!.removeEventListener("wheel", get().handleWheel);
    canvas!.removeEventListener("pointerdown", get().handlePointerDown);
    canvas!.removeEventListener("pointermove", get().handlePointerMove);
    canvas!.removeEventListener("pointerup", get().handlePointerUp);
    canvas!.removeEventListener("pointercancel", get().handlePointerCancel);
    canvas!.removeEventListener("contextmenu", (e) => {
      e.preventDefault();
    });
  },

  handleKeyDown(e: KeyboardEvent) {
    const { zoomRange, disableKeyboardShortcuts } = get();
    if (zoomRange || disableKeyboardShortcuts) {
      return;
    }

    if (e.code === "Space") {
      set({
        asynchronousScrolling: true,
      });

      // We are drawing the tracks highlight-overlay
      get().refreshMain();
      e.preventDefault();
      return;
    }

    switch (e.key) {
      case "ArrowUp": {
        get().handleScroll(0, e.shiftKey ? -100 : -10);
        break;
      }
      case "ArrowDown": {
        get().handleScroll(0, e.shiftKey ? 100 : 10);
        break;
      }
      case "ArrowLeft": {
        get().handleScroll(e.shiftKey ? -100 : -10, 0);
        break;
      }
      case "ArrowRight": {
        get().handleScroll(e.shiftKey ? 100 : 10, 0);
        break;
      }
      case "c": {
        get().clearRangeSelection();
        break;
      }
    }
  },

  handleKeyUp(e: KeyboardEvent) {
    if (e.code === "Space") {
      set({
        asynchronousScrolling: false,
      });

      get().refreshMain();
      e.preventDefault();
      return;
    }

    if (e.key === "Control") {
      set({
        zoomRange: undefined,
      });

      get().refreshMain();
      e.preventDefault();
      return;
    }
  },

  handleFeaturePopover(e: PointerEvent) {
    const el = document.getElementById("genome-viewer-contextual-menu");
    const { showFeaturePopover } = get();
    if (!el) {
      return;
    }
    if (!isChildOf(e.target as HTMLElement, el as HTMLElement) && showFeaturePopover) {
      const contextualMenu = document.getElementById("genome-viewer-contextual-menu");
      if (contextualMenu) {
        set({ showFeaturePopover: false, contextualMenuData: undefined });
        Object.assign(contextualMenu.style, {
          display: "none",
        });
      }
    }
  },
});

// Debouncing the scroll, in order to refresh the "Current region" in the table
export const handleScrollingEnd = debounce((set: StoreApi<GenomeViewerStore>["setState"]) => {
  set({ isScrolling: false });
}, 600);
